<script setup lang="ts">
import { computed, ref, watch } from 'vue';
import { useFilterOptions } from '@/composables/api/useFilterOptions';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuCheckboxItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { LazyTooltip } from '@/components/ui/tooltip';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { ChevronDownIcon, MagnifyingGlassIcon } from '@heroicons/vue/24/solid';

interface Props {
  modelValue?: number[];
  slug: string;
  disabled?: boolean;
}

const props = withDefaults(defineProps<Props>(), {
  modelValue: () => [],
  disabled: false,
});

const emit = defineEmits<{
  'update:modelValue': [value: number[]];
  /**
   * Human-readable labels for the current selection, in selection order, with
   * a plain `String(id)` fallback while options are still loading. Lets the
   * host RuleRow snapshot names for id-valued fields when a row is starred as
   * a favorite (SAU-260).
   */
  'update:selectedLabels': [labels: string[]];
}>();

const searchQuery = ref('');
const { data: options, isLoading } = useFilterOptions({
  slug: props.slug,
  searchQuery: searchQuery,
  enabled: computed(() => Boolean(props.slug))
});

// Filter options based on search query
const filteredOptions = computed(() => {
  if (!options.value) return [];
  if (!searchQuery.value) return options.value;
  
  const query = searchQuery.value.toLowerCase();
  return options.value.filter((option: { id: number; name?: string; display_string?: string }) => {
    const name = option.name?.toLowerCase() || '';
    const displayString = option.display_string?.toLowerCase() || '';
    return name.includes(query) || displayString.includes(query);
  });
});

// Check if a specific option is selected
const isSelected = (optionId: number): boolean => {
  return props.modelValue.includes(optionId);
};

// Toggle a specific option
const toggleOption = (optionId: number, checked: boolean) => {
  if (props.disabled) return;
  
  const currentValues = [...props.modelValue];
  const index = currentValues.indexOf(optionId);
  
  if (checked && index === -1) {
    // Add if not already in the list
    currentValues.push(optionId);
  } else if (!checked && index > -1) {
    // Remove if it's in the list
    currentValues.splice(index, 1);
  }
  
  emit('update:modelValue', currentValues);
};

// Get selected option names for display
const selectedOptions = computed(() => {
  if (!options.value) return [];
  return options.value.filter((option: { id: number; name?: string; display_string?: string }) => props.modelValue.includes(option.id));
});

// Labels for the selected ids in selection order, falling back to the raw id
// when its option hasn't loaded (yet). Emitted so the host can snapshot
// human-readable names (see `update:selectedLabels` above).
const selectedLabels = computed(() =>
  props.modelValue.map((selectedId) => {
    const option = (options.value ?? []).find(
      (candidate: { id: number; name?: string; display_string?: string }) => candidate.id === selectedId,
    );
    return option?.display_string || option?.name || String(selectedId);
  }),
);

watch(selectedLabels, (labels) => emit('update:selectedLabels', labels), { immediate: true });

// Display text for the button
const displayText = computed(() => {
  if (props.modelValue.length === 0) {
    return 'Select options';
  }
  if (props.modelValue.length === 1) {
    const option = selectedOptions.value[0];
    return option?.display_string || option?.name || String(props.modelValue[0]);
  }
  return `${props.modelValue.length} selected`;
});

// Tooltip text showing all selected items
const tooltipText = computed(() => {
  return selectedOptions.value
    .map((option: { id: number; name?: string; display_string?: string }) => option.display_string || option.name || String(option.id))
    .join(', ');
});
</script>

<template>
  <!-- The tooltip lists what is picked. It wraps the whole menu, not just its
       trigger, so the tooltip and the menu each keep their own anchor. See
       LazyTooltip for why. -->
  <LazyTooltip side="top" align="start" content-class="max-w-xs" :enabled="props.modelValue.length > 0">
    <DropdownMenu>
      <DropdownMenuTrigger as-child>
        <Button
          variant="outline"
          :disabled="disabled || isLoading"
          class="w-full text-left font-normal pl-2 pr-4 [&>div]:w-full [&>div>div]:w-full [&>div>div]:justify-between"
          data-testid="model-array-combobox-trigger"
        >
          <span class="truncate min-w-0">{{ displayText }}</span>
          <ChevronDownIcon class="h-4 w-4 shrink-0 opacity-50" />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent class="w-56" align="start">
        <div class="p-2">
          <div class="relative">
            <MagnifyingGlassIcon class="absolute left-2 top-2.5 h-4 w-4 text-muted-foreground" />
            <Input
              v-model="searchQuery"
              placeholder="Search..."
              class="pl-8"
              data-testid="model-array-combobox-search"
              @click.stop
            />
          </div>
        </div>
        <div class="max-h-60 overflow-y-auto">
          <DropdownMenuCheckboxItem
            v-for="option in filteredOptions"
            :key="option.id"
            :model-value="isSelected(option.id)"
            @update:model-value="(checked: boolean) => toggleOption(option.id, checked)"
            @select.prevent
            :data-testid="`model-array-option-${option.id}`"
          >
            {{ option.display_string || option.name || option.id }}
          </DropdownMenuCheckboxItem>
          <div v-if="filteredOptions.length === 0 && !isLoading" class="px-2 py-1.5 text-sm text-muted-foreground">
            No options found
          </div>
          <div v-if="isLoading" class="px-2 py-1.5 text-sm text-muted-foreground">
            Loading...
          </div>
        </div>
      </DropdownMenuContent>
    </DropdownMenu>
    <template #content>
      <p>{{ tooltipText }}</p>
    </template>
  </LazyTooltip>
</template>




